import React, { useEffect, useState } from "react";
import { X, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { getGuestEmail } from "./GuestEmailCapture";
import { toast } from "sonner";

const DISMISS_KEY = "taven_exit_prompt_dismissed";
const valid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// Gentle exit-intent prompt for guests with items in the cart. Fires once
// per session when the mouse leaves toward the top of the viewport.
// Fully TAVEN-branded, non-aggressive, mobile-safe (mobile uses a delayed nudge).
export const ExitIntentCapture = () => {
  const { user } = useAuth();
  const { items, subtotal } = useCart();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");

  const eligible = !user && items.length > 0 && !getGuestEmail();

  useEffect(() => {
    if (!eligible) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    const trigger = () => {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
      setOpen(true);
      sessionStorage.setItem(DISMISS_KEY, "1");
    };
    const onLeave = (e) => { if (e.clientY <= 0) trigger(); };
    document.addEventListener("mouseout", onLeave);
    // mobile fallback: gentle nudge after 25s of dwelling on cart
    const t = setTimeout(() => { if (window.innerWidth < 768) trigger(); }, 25000);
    return () => { document.removeEventListener("mouseout", onLeave); clearTimeout(t); };
  }, [eligible]);

  if (!open) return null;

  const close = () => setOpen(false);

  const save = async (e) => {
    e.preventDefault();
    if (!valid(email)) { toast.error("Please enter a valid email address."); return; }
    try {
      await api.post("/cart/track", {
        email,
        items: items.map((i) => ({ handle: i.handle, title: i.title, image: i.image, price: i.price, quantity: i.quantity })),
        subtotal,
      });
      localStorage.setItem("taven_guest_email", email);
      toast.success("Saved! We'll hold your cart for you.");
      close();
    } catch { toast.error("Could not save right now. Please try again."); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="exit-intent-modal" onClick={close}>
      <div className="bg-white max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} data-testid="exit-intent-close" className="absolute top-4 right-4 text-black/40 hover:text-black"><X className="w-5 h-5" /></button>
        <Mail className="w-10 h-10 text-taven-gold" />
        <h2 className="text-2xl font-black font-display mt-4">Wait — save your cart?</h2>
        <p className="text-sm text-muted-foreground mt-2">Leave your email and we'll hold your items and send a gentle reminder so you don't lose your picks. No spam, ever.</p>
        <form onSubmit={save} className="mt-5 flex flex-col gap-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="exit-intent-input"
            className="border border-black/15 px-4 py-3 text-sm outline-none focus:border-taven-gold" />
          <button type="submit" data-testid="exit-intent-submit" className="bg-taven-orange text-white font-bold uppercase tracking-wider py-3.5 hover:bg-[#d9561a] transition-colors">Save My Cart</button>
          <button type="button" onClick={close} className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-1 hover:text-black">No thanks</button>
        </form>
        <p className="text-[11px] text-muted-foreground text-center mt-3">Need help? support@taven.store</p>
      </div>
    </div>
  );
};

export default ExitIntentCapture;
